import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { updateProfile } from '../../store/reducers/userSlice';
import { setMessage } from '../../store/reducers/appSlice';
import { createShop } from '../../lib/api/shop/create_shop';
import { fetchAppInit } from '../../lib/api/app-init';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import AIURLInput from '../../ui/inputs/AIURLInput';
import { submitLogToBugsnag } from '../../lib/api/log';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import { SHOP_API } from '../../lib/constants/central-api';
import { asyncHandler } from '../../lib/api/async-handler';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { RequestInfo } from '../../interfaces/RequestInfo';

interface AddClientFormProps {
  onClose: () => void;
  onBrandLimitReached?: () => void;
}

const AddClientForm: React.FC<AddClientFormProps> = ({
  onClose,
  onBrandLimitReached,
}) => {
  const dispatch = useDispatch();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const seller_email = useSelector(
    (state: RootState) => state.user.profile.email
  );
  const isWhiteLabel = useSelector((state: RootState) => state.UI.isWhiteLabel);

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url || !isNotEmptyString(url)) return;

    setLoading(true);

    try {
      const tempName = `${seller_email}'s shop`;
      const shopResponse = await createShop(tempName);

      if (shopResponse.isLimitReached) {
        setLoading(false);
        onBrandLimitReached?.();
        return;
      }

      if (!shopResponse.success || !shopResponse.data) {
        throw new Error(shopResponse.message || 'Shop creation failed');
      }

      const shopSlug = shopResponse.data.slug;

      // Save webshop URL
      const webshopUrl = url.startsWith('http') ? url : `https://${url}`;
      const requestInfo: RequestInfo = {
        url: SHOP_API + `/${shopSlug}/setting/shop-general`,
        method: 'PUT',
        body: applicationWWWXRLEncodedBodyBuilder({ webshop_url: webshopUrl }),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: `${accessToken.type} ${
            isWhiteLabel
              ? accessToken.currentWhiteLabelSellerToken
              : accessToken.sellerToken
          }`,
        },
      };

      await asyncHandler(
        requestInfo,
        'SAVE_WEBSHOP_DATA',
        'Website saved.',
        false,
        false,
        true
      );

      dispatch(updateProfile({ active_shop_slug: shopSlug }));
      fetchAppInit(true);
      onClose();
    } catch (error: any) {
      console.error('Error creating client:', error);
      submitLogToBugsnag('error', `Error creating client: ${error}`);
      dispatch(
        setMessage({
          message: error.message || 'Failed to create client',
          messageType: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submitForm} className='addClientForm'>
      <h3 className='addClientForm__title'>Add new client</h3>
      <div className='addClientForm__input'>
        <AIURLInput
          id='addClientForm__url-input'
          value={url}
          prefix='https://'
          changeListeners={[
            (e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value),
          ]}
          placeholder='Enter website URL (e.g. example.com)'
          label=''
        />
      </div>
      <div className='addClientForm__actions'>
        <button
          type='button'
          className='addClientForm__cancel'
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <MainSquareButton
          type='submit'
          value='Add Client'
          normal={true}
          loading={loading}
          disabled={!url || !isNotEmptyString(url)}
        />
      </div>
    </form>
  );
};

export default AddClientForm;
